import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { distanceMiles } from "../../supabase/functions/_shared/picksRanking.ts";

interface LocationResult {
  lat: number;
  lng: number;
  description: string;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  types?: string[];
}

interface LocationBias {
  lat: number;
  lng: number;
  radiusMeters?: number;
}

const IDLE_RESET_MS = 3 * 60 * 1000;

function newSessionToken(): string {
  // RFC4122 v4 via crypto.randomUUID when available.
  const anyCrypto = (globalThis as any).crypto;
  if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
  // Fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function useLocationSearch() {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationResult | null>(() => {
    try {
      const stored = localStorage.getItem("grounded_browse_location");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed.lat === "number" && typeof parsed.lng === "number" && typeof parsed.description === "string") {
          return { lat: parsed.lat, lng: parsed.lng, description: parsed.description };
        }
      }
    } catch {
      // ignore invalid stored data
    }
    return null;
  });

  // Session token lifecycle: minted lazily on first keystroke, reused across
  // all autocomplete calls, passed to geometry-details on selection, then
  // reset. Idle >3min also resets. Grouping ties the v1 autocomplete calls to
  // the v1 Place Details call as one Per-Session SKU.
  const sessionTokenRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(0);

  const getSessionToken = useCallback(() => {
    const now = Date.now();
    if (!sessionTokenRef.current || now - lastActivityRef.current > IDLE_RESET_MS) {
      sessionTokenRef.current = newSessionToken();
    }
    lastActivityRef.current = now;
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
    lastActivityRef.current = 0;
  }, []);

  const searchLocations = useCallback(
    async (query: string, locationBias?: LocationBias): Promise<PlacePrediction[]> => {
      if (!query || query.length < 2) {
        setPredictions([]);
        return [];
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase.functions.invoke("google-places", {
          body: {
            action: "autocomplete",
            query,
            searchType: "regions",
            sessionToken: getSessionToken(),
            locationBias,
          },
        });

        if (error) throw error;

        const locationPredictions = data?.predictions ?? [];
        const sliced = locationPredictions.slice(0, 5) as PlacePrediction[];
        setPredictions(sliced);
        return sliced;
      } catch (err) {
        console.error("[useLocationSearch] Search failed:", err);
        setPredictions([]);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [getSessionToken],
  );

  const selectLocation = useCallback(
    async (placeId: string, description: string) => {
      setIsSearching(true);
      try {
        const token = sessionTokenRef.current ?? getSessionToken();
        const { data, error } = await supabase.functions.invoke("google-places", {
          body: {
            action: "details",
            detailsMode: "geometry",
            placeId,
            sessionToken: token,
          },
        });

        // Close the billing session regardless of outcome.
        resetSessionToken();

        if (error) throw error;

        const location = data?.result?.geometry?.location;
        if (location) {
          setSelectedLocation({
            lat: location.lat,
            lng: location.lng,
            description,
          });
          setPredictions([]);
          return { lat: location.lat, lng: location.lng, description };
        }
        return null;
      } catch (err) {
        console.error("[useLocationSearch] Get details failed:", err);
        return null;
      } finally {
        setIsSearching(false);
      }
    },
    [getSessionToken, resetSessionToken],
  );

  const clearLocation = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  const clearAll = useCallback(() => {
    setSelectedLocation(null);
    setPredictions([]);
    resetSessionToken();
  }, [resetSessionToken]);

  return {
    predictions,
    isSearching,
    selectedLocation,
    searchLocations,
    selectLocation,
    clearLocation,
    clearAll,
  };
}

/**
 * Calculate distance between two coordinates in miles
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  return distanceMiles(lat1, lon1, lat2, lon2);
}
